import { Mesh, MeshBuilder, StandardMaterial, Texture } from "@babylonjs/core"
import { log } from "../../../common/Logging"
import { mazeImages } from "./assets/MazeImages"
import { MazeCigs } from "./MazeCigs"
import { Coor, MazeGrid } from "./MazeGrid"
import { MazeStore } from "./MazeStore"

export interface Wall extends Coor {
    vertical?: boolean
    halfHeight?: boolean
    belowGround?: boolean
}

export class MazeWalls {
    static readonly WALLS_WIDE = 9

    wallCoors: Array<Wall> = []

    wallMeshes: Array<Mesh> = []
    portcullis: Mesh

    constructor(
        private portcullisUp: boolean,
        private wallsLong: number,
        private horizontalWalls: Array<Array<number>>,
        private verticalWalls: Array<Array<number>>,
    ) {
        const { scene } = MazeStore.instance

        const bricksMaterial = new StandardMaterial("bricks", scene)
        bricksMaterial.diffuseTexture = new Texture(mazeImages.bricks, scene)
        bricksMaterial.backFaceCulling = true

        const wallSize = 10
        const halfWallSize = wallSize / 2

        // x and z are like coordinates in a grid
        const wallMaker = (wallInfo: Wall) => {
            this.wallCoors.push(wallInfo)
            const { x, z, vertical, halfHeight, belowGround } = wallInfo
            const wall = MeshBuilder.CreateBox(
                `wall (${x},${z}) vertical: ${vertical}`,
                { height: wallSize, width: wallSize, depth: 0.5 },
                scene,
            )
            wall.material = bricksMaterial
            wall.isPickable = true

            const wallPosition = MazeGrid.getPositionOnGrid({ x, z })
            wall.position.x = wallPosition.x + (vertical ? -halfWallSize : 0)
            wall.position.z = wallPosition.z + (vertical ? halfWallSize : 0)

            let height
            if (belowGround) {
                height = -6
            } else if (halfHeight) {
                height = 0
            } else {
                height = halfWallSize
            }

            wall.position.y = height
            wall.checkCollisions = true
            if (vertical) {
                wall.addRotation(0, Math.PI / 2, 0)
            }
            this.wallMeshes.push(wall)
            return wall
        }

        // Outer horizontal walls.
        for (let x = 0; x < MazeWalls.WALLS_WIDE; x++) {
            if (x !== 4) {
                wallMaker({ x, z: 0 })
            } else {
                this.portcullis = wallMaker({
                    x,
                    z: 0,
                    belowGround: !portcullisUp,
                })
            }
            if (x !== 0) {
                wallMaker({ x, z: this.wallsLong })
            }
        }
        // Outer vertical walls.
        for (let x = 0; x < this.wallsLong; x++) {
            wallMaker({ x: 0, z: x, vertical: true })
            wallMaker({ x: MazeWalls.WALLS_WIDE, z: x, vertical: true })
        }

        // Courtyard horizontal walls.
        for (let x = 1; x < MazeWalls.WALLS_WIDE - 1; x++) {
            wallMaker({ x, z: -7, halfHeight: true })
        }

        // Courtyard vertical walls.
        for (let x = -1; x > -8; x--) {
            wallMaker({ x: 1, z: x, vertical: true, halfHeight: true })
            wallMaker({
                x: MazeWalls.WALLS_WIDE - 1,
                z: x,
                vertical: true,
                halfHeight: true,
            })
        }

        this.horizontalWalls.forEach((wallRow, idx) => {
            wallRow.forEach((wallX) => wallMaker({ x: wallX, z: idx + 1 }))
        })
        this.verticalWalls.forEach((wallRow, idx) => {
            wallRow.forEach((wallX) =>
                wallMaker({ x: wallX + 1, z: idx, vertical: true }),
            )
        })

        return this
    }

    movePortcullis = async (up: boolean) => {
        return new Promise<void>((resolve) => {
            const moveAmount = up ? 11 : -11
            const moveIncrement = up ? 0.1 : -0.1
            const start = this.portcullis.position.y
            const move = () => {
                const intervalId = window.requestAnimationFrame(move)
                if (
                    (up && this.portcullis.position.y >= start + moveAmount) ||
                    (!up && this.portcullis.position.y <= start + moveAmount)
                ) {
                    this.portcullis.position.y = start + moveAmount
                    cancelAnimationFrame(intervalId)
                    resolve()
                } else {
                    this.portcullis.position.y += moveIncrement
                }
            }
            move()
        })
    }

    destroy = () => {
        log.debug("Destroying the maze!!!")
        this.wallMeshes.forEach((wallMesh) => wallMesh.dispose())
    }
}

export const mazeBuilders = {
    // These are built from the bottom up. Each array is for a row. The values in the arrays are the horizontal or vertical wall's place in the maze, from 0
    // up to the maze's width - 1. You should have rows equal to walls long - 1
    exampleMazeWalls: () =>
        new MazeWalls(
            false,
            8,
            [
                [1, 2, 4, 5, 7],
                [1, 4, 6, 7],
                [0, 1, 2, 3, 6],
                [1, 2, 5, 7, 8],
                [1, 3, 4, 5],
                [0, 1, 2, 3, 5, 6, 7, 8],
                [2, 4, 5, 7],
            ],
            [
                [2, 6],
                [2, 3, 5],
                [1, 4, 7],
                [3, 4, 6],
                [2, 5, 6],
                [1, 7],
                [3, 6],
                [0, 2],
            ],
        ),

    realMaze: () => {
        const walls = new MazeWalls(
            true,
            20,
            [
                [1, 3, 4, 5, 6], // 1
                [0, 5, 6, 7], // 2
                [3, 5, 7], // 3
                [0, 2, 3, 7], // 4
                [1, 3, 4, 7], // 5
                [0, 1, 4, 7, 8], // 6
                [1, 3, 4, 6, 7], // 7
                [0, 1, 3, 7], // 8
                [1, 2, 4, 6], // 9
                [0, 2], // 10
                [1, 3, 5, 6], // 11
                [0, 1, 2, 5, 7], // 12
                [1, 3, 4, 5, 6, 8], // 13
                [2, 4], // 14
                [0, 3, 4, 6, 7], // 15
                [1, 4, 7], // 16
                [0, 1, 3, 5, 7], // 17
                [1, 2, 3, 4, 5, 6, 7, 8], // 18
                [0, 1, 2, 3, 4, 5, 6, 7], // 19
            ],
            [
                [1], // 1
                [1, 2, 3, 5, 7], // 2
                [0, 3, 4, 6], // 3
                [1, 3], // 4
                [4, 5, 7], // 5
                [1, 2, 5, 6], // 6
                [2, 4, 5, 7], // 7
                [1, 2, 4, 5, 6], // 8
                [4, 5], // 9
                [0, 3, 4, 5, 6, 7], // 10
                [2, 4, 6, 7], // 11
                [1, 2, 3, 7], // 12
                [2, 5, 6], // 13
                [0, 1, 5, 7], // 14
                [2, 3, 5, 6], // 15
                [0, 1, 2], // 16
                [2, 3, 4, 5, 7], // 17
                [3, 6], // 18
                [], // 19
                [], // 20
            ],
        )
        return {
            walls,
            cigs: MazeCigs.forRealMaze(walls.wallCoors),
            pathFinder: MazeGrid.createMazePathingGraph(20, walls.wallCoors),
        }
    },
}
